import { gql, useQuery } from '@provision/graphql-client';
import { useMemo } from 'react';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { useCurrentUserProfile } from 'src/graphql/entities/platform-user';
import { SignalRulesConfig } from 'src/graphql/entities/signal-oncall-schedule/signal-oncall-schedule.types';
import { deepCloneAndOmit } from 'src/utils/objects';

const GET_SIGNAL_RULES_CONFIG_QUERY = gql`
  query GetSignalRulesConfig {
    getSignalRulesConfig {
      version
      conditionTypes {
        label
        value
      }
      operators {
        text {
          value
          label
          arity
        }
        textarea {
          value
          label
          arity
        }
        list {
          value
          label
          arity
        }
        number {
          value
          label
          arity
        }
        date {
          value
          label
          arity
        }
        boolean {
          value
          label
          arity
        }
      }
      conditions {
        data {
          triggers
          group
          attribute
          attributeLabel
          uiType
          operators {
            value
            label
            arity
          }
          options {
            value
            label
          }
          optionsQuery {
            graphqlQuery
          }
        }
      }
    }
  }
`;

interface GetSignalRulesConfigQuery {
  getSignalRulesConfig: SignalRulesConfig & { __typename: string };
}

export const useSignalRulesConfig = () => {
  const { user } = useCurrentUserProfile();
  const { data, loading } =
    useQuery<GetSignalRulesConfigQuery>(GET_SIGNAL_RULES_CONFIG_QUERY, {
      context: {
        includePlatformLevelData: user?.partnerId === PLATFORM_PARTNER_ID,
      },
    });

  const config = useMemo<SignalRulesConfig|null>(
    () =>
      (data?.getSignalRulesConfig && !loading)
        ? deepCloneAndOmit<GetSignalRulesConfigQuery['getSignalRulesConfig']>(
          data?.getSignalRulesConfig,
          ['__typename'],
        )
        : null,
    [data?.getSignalRulesConfig, loading],
  );

  return {
    config,
    loading,
  };
};
